"use client";
import {
  FileCode2,
  FileWarning,
  RefreshCw,
} from "lucide-react";
import {
  useRef,
  useState,
} from "react";
import {
  clearYaraRules,
  getYaraState,
  installYaraRules,
  parseYaraRules,
  YARA_SUPPORTED_SUMMARY,
  YARA_UNSUPPORTED_CONSTRUCTS,
} from "../../../lib/yara";
import {
  type ScanReport,
} from "../../../lib/types";


export function YaraRulesView({
  report,
  onRescan,
}: {
  report: ScanReport | null;
  onRescan?: () => void;
}) {
  const [source, setSource] = useState("");
  const [namespace, setNamespace] = useState("local.yar");
  const [error, setError] = useState("");
  // The installed rule set lives in module state, not here: a rescan unmounts
  // this view, and a local copy would be lost exactly when it is needed to
  // decide whether the rules have been evaluated. This is only a render bump.
  const [, setRevision] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = (text: string, name: string) => {
    setError("");
    if (!text.trim()) {
      setError("Paste a rule file or choose a .yar file first.");
      return;
    }
    const parsed = parseYaraRules(name || "local.yar", text);
    installYaraRules(parsed, false, name || "local.yar");
    setRevision((value) => value + 1);
    if (!parsed.rules.length && parsed.unsupported.length) {
      setError(
        "No rules in this file could run. See the unsupported list below.",
      );
    }
  };

  const clear = () => {
    clearYaraRules();
    setRevision((value) => value + 1);
    setSource("");
    setError("");
  };

  const state = getYaraState();
  const unsupported = state.summaries.length || state.unsupported.length
    ? state.unsupported
    : (report?.yaraUnsupported ?? []);
  const rules = state.summaries;
  const currentGeneration = state.generation;
  // A rule set loaded after the scan has never been evaluated against this
  // sample. Reporting "no match" there would be a claim about a scan that
  // never happened, which is the one thing this view must not do.
  const staleRules = Boolean(
    report &&
      (currentGeneration !== 0 || report.yaraRulesLoaded > 0) &&
      currentGeneration !== report.yaraGeneration,
  );
  const matches = staleRules ? [] : (report?.yaraMatches ?? []);

  return (
    <div className="yara-workspace">
      <input
        accept=".yar,.yara,.rule,.txt"
        className="file-input"
        onChange={async (event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          const text = await file.text();
          setSource(text);
          setNamespace(file.name);
          load(text, file.name);
          event.target.value = "";
        }}
        ref={inputRef}
        type="file"
      />

      {/* The supported subset is stated where the rules are entered, not only
          in the docs. An analyst has to know what the engine can honour before
          they trust it with their own rules. */}
      <section className="yara-subset panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">ENGINE CAPABILITY</div>
            <h3>What this YARA engine supports</h3>
          </div>
          <span className="yara-subset-tag">browser subset</span>
        </div>
        <div className="yara-subset-body">
          <p>
            JarLab runs YARA itself, in the browser. There is no{" "}
            <code>yara</code> binary behind this, so it implements a subset. A
            rule using anything outside that subset is <strong>refused and named</strong>{" "}
            below rather than quietly matching nothing.
          </p>
          <div className="yara-capability">
            <span>Supported</span>
            <p>{YARA_SUPPORTED_SUMMARY}</p>
          </div>
          <div className="yara-capability is-out">
            <span>Refused by name</span>
            <p>{YARA_UNSUPPORTED_CONSTRUCTS.join(" · ")}</p>
          </div>
        </div>
      </section>

      <div className="yara-grid">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">YOUR RULES</div>
              <h3>Load rule source</h3>
            </div>
            {(state.summaries.length > 0 || state.unsupported.length > 0) && (
              <button className="quiet-button" onClick={clear} type="button">
                Unload all
              </button>
            )}
          </div>
          <div className="yara-editor">
            <label htmlFor="yara-namespace">File name</label>
            <input
              id="yara-namespace"
              onChange={(event) => setNamespace(event.target.value)}
              placeholder="internal.yar"
              value={namespace}
            />
            <label htmlFor="yara-source">Rule source</label>
            <textarea
              id="yara-source"
              onChange={(event) => setSource(event.target.value)}
              placeholder={
                'rule My_Loader {\n  meta:\n    severity = "high"\n  strings:\n    $a = "/shard/prefire"\n  condition:\n    $a\n}'
              }
              spellCheck={false}
              value={source}
            />
            <div className="yara-actions">
              <button
                className="primary-button"
                onClick={() => load(source, namespace)}
                type="button"
              >
                <FileCode2 size={15} />
                Load rules
              </button>
              <button
                className="secondary-button"
                onClick={() => inputRef.current?.click()}
                type="button"
              >
                Choose .yar file
              </button>
            </div>
            {error && <p className="yara-error">{error}</p>}
          </div>
        </section>

        <section className="panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">LOADED</div>
              <h3>{rules.length} rules running</h3>
            </div>
          </div>
          {rules.length ? (
            <ul className="yara-rule-list">
              {rules.map((rule) => (
                <li key={rule.ruleId}>
                  <code>{rule.ruleId}</code>
                  <span>
                    {rule.stringCount} string{rule.stringCount === 1 ? "" : "s"}
                    {rule.tags.length ? ` · ${rule.tags.join(", ")}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="yara-empty">
              No custom rules loaded. JarLab&rsquo;s built-in detections run
              regardless.
            </p>
          )}
        </section>
      </div>

      {/* Unsupported is the loudest thing on the page, and deliberately so: a
          rule that quietly did nothing is worse than no rule at all, because
          the analyst believes they were covered. */}
      <section
        className={`panel yara-unsupported${unsupported.length ? " has-problems" : ""}`}
      >
        <div className="panel-heading">
          <div>
            <div className="section-kicker">
              DID NOT RUN
            </div>
            <h3>
              {unsupported.length
                ? `${unsupported.length} rule${unsupported.length === 1 ? "" : "s"} could not run`
                : "No unsupported rules"}
            </h3>
          </div>
          {unsupported.length > 0 && <FileWarning size={19} />}
        </div>
        {unsupported.length ? (
          <>
            <p className="yara-unsupported-lead">
              These rules did not contribute to this scan. Anything they would
              have matched is <strong>not covered</strong> by them.
            </p>
            <ul className="yara-unsupported-list">
              {unsupported.map((item, index) => (
                <li key={`${item.rule}-${item.construct}-${index}`}>
                  <div>
                    <code>{item.rule}</code>
                    <span className="yara-construct">{item.construct}</span>
                    <small>line {item.line}</small>
                  </div>
                  <p>{item.detail}</p>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="yara-empty">
            Every rule you loaded uses constructs this engine honours.
          </p>
        )}
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">THIS SCAN</div>
            <h3>
              {staleRules
                ? "Rules have not run"
                : `${matches.length} rule match${matches.length === 1 ? "" : "es"}`}
            </h3>
          </div>
        </div>
        {staleRules ? (
          <div className="yara-stale">
            <p>
              These rules were loaded after{" "}
              <strong>{report?.fileName}</strong> was scanned, so they have
              not been evaluated against it. This sample has{" "}
              <strong>no result</strong> for them yet — that is not the same as
              a clean result.
            </p>
            {onRescan ? (
              <button type="button" className="button" onClick={onRescan}>
                <RefreshCw size={14} aria-hidden="true" />
                Rescan with these rules
              </button>
            ) : (
              <p className="yara-stale-note">
                Reopen the sample to evaluate them.
              </p>
            )}
          </div>
        ) : matches.length ? (
          <ul className="yara-match-list">
            {matches.map((match) => (
              <li key={match.ruleId}>
                <div>
                  <code>{match.ruleId}</code>
                  <span className={`severity-label tone-${match.severity}`}>
                    {match.severity}
                  </span>
                </div>
                <p>
                  {match.target}
                  {match.surface ? (
                    <span className="yara-surface">{match.surface}</span>
                  ) : null}
                </p>
                <small>
                  {match.strings.map((item) => item.identifier).join(" ")}
                </small>
              </li>
            ))}
          </ul>
        ) : (
          <p className="yara-empty">
            {report
              ? "No loaded rule matched this sample."
              : "Scan a sample to see which rules fire."}
          </p>
        )}
      </section>
    </div>
  );
}
